import { prop } from '@ipfabric/utils/fp/prop';
import { useGlobalStateContextSelectors } from 'components/GlobalState/GlobalStateModel';
import { initializeApp } from 'components/GlobalState/commands/initializeApp';
import { selectAppInitialized } from 'components/GlobalState/selectors/selectAppInitialized';
import { useSocket } from 'components/Socket/useSocket';
import { SocketDisconnected } from 'components/SocketDisconnected/SocketDisconnected';
import { useLocale } from 'containers/App/LocaleContext';
import { AppLoader } from 'containers/AppLoader/AppLoader';
import { Maintenance } from 'pages/Maintenance/Maintenance';
import { useEffect } from 'react';
import type { FC, ReactNode } from 'react';

interface Props {
  children?: ReactNode;
}

/**
 * Load initial app data needed to display the app
 */
export const AppInitializer: FC<Props> = (props) => {
  const ctxProps = useGlobalStateContextSelectors({
    appInitialized: selectAppInitialized,
    appLoadError: prop('appLoadError'),
    dispatch: prop('dispatch'),
    maintenanceRunning: prop('maintenanceRunning'),
    socketState: prop('socketState'),
  });

  useSocket();

  const appInitializerMsg = useLocale().appInitializer;

  const apiNotAvailable =
    ctxProps.maintenanceRunning || ctxProps.socketState === 'DISCONNECTED';

  useEffect(
    // try initialization on start, then retry every time api becomes available
    () => {
      if (apiNotAvailable) {
        return;
      }
      ctxProps.dispatch(initializeApp);
    },
    [apiNotAvailable, ctxProps.dispatch],
  );

  const renderContent = () => {
    if (ctxProps.appInitialized) {
      return props.children;
    }
    if (ctxProps.appLoadError) {
      return (
        <div className='match-parent flex items-center justify-center text-red-800'>
          {appInitializerMsg.error}
        </div>
      );
    }
    return <AppLoader />;
  };

  return (
    <>
      {/* overlays when api is not available */}
      {ctxProps.socketState === 'DISCONNECTED' && <SocketDisconnected />}
      {ctxProps.maintenanceRunning && <Maintenance />}

      {renderContent()}
    </>
  );
};
